import React, { useMemo } from "react";
import { evaluatePickAgainstScore } from "@/lib/pickStatus";
import { Bell, BellSlash } from "@phosphor-icons/react";

const LIVE_CODES = new Set([
    "1H",
    "HT",
    "2H",
    "ET",
    "BT",
    "P",
    "SUSP",
    "INT",
    "LIVE",
]);
const FINISHED_CODES = new Set(["FT", "AET", "PEN"]);

const classify = (pick, live) => {
    if (!live) return { phase: "upcoming", bet: null };
    const short = (live.status?.short || "").toUpperCase();
    if (FINISHED_CODES.has(short)) {
        return {
            phase: "finished",
            bet: evaluatePickAgainstScore(pick, live.goals?.home, live.goals?.away),
        };
    }
    if (LIVE_CODES.has(short)) {
        return {
            phase: "live",
            bet: evaluatePickAgainstScore(pick, live.goals?.home, live.goals?.away),
        };
    }
    return { phase: "upcoming", bet: null };
};

/**
 * Real-time rollup of *all* of today's accessible picks.
 *
 * - "Active": picks whose match is in-play right now.
 * - "On track": live picks where the current score satisfies the bet
 *   PLUS finished picks that already won.
 * - "Slipping": live picks losing right now PLUS finished losses.
 * - "Projected payout": running accumulator on `1 unit` stake assuming
 *   every still-alive pick (`on_track`, `upcoming`, `push`) settles green.
 *   Any slipping/lost pick zeroes the slip.
 *
 * Renders nothing if there are no accessible picks yet — caller decides
 * placement; we own the visuals only.
 */
export default function SlipScoreboard({
    tiers,
    liveByFixture,
    userLevel,
    alerts,
}) {
    const stats = useMemo(() => {
        const accessibleTiers =
            userLevel === "vip" ? ["2", "5", "10"] : ["2"];
        const picks = accessibleTiers
            .flatMap((t) => tiers?.[t] || [])
            .filter((p) => p && p.fixtureId);
        if (!picks.length) return null;

        let live = 0;
        let onTrack = 0;
        let slipping = 0;
        let won = 0;
        let lost = 0;
        let pendingKickoff = 0;
        let slipAlive = true;
        let projected = 1;

        for (const p of picks) {
            const liveRow = liveByFixture?.[String(p.fixtureId)];
            const { phase, bet } = classify(p, liveRow);
            const odds = Number(p.odds || 0) || 1;

            if (phase === "live") {
                live += 1;
                if (bet === "on_track") {
                    onTrack += 1;
                    projected *= odds;
                } else if (bet === "slipping") {
                    slipping += 1;
                    slipAlive = false;
                } else if (bet === "tight") {
                    // DNB push — pick refunds, neutral on accumulator.
                    projected *= 1;
                } else {
                    // Unknown bet shape during live play — be conservative,
                    // treat as still alive at face odds.
                    projected *= odds;
                }
            } else if (phase === "finished") {
                if (bet === "on_track") {
                    won += 1;
                    onTrack += 1;
                    projected *= odds;
                } else if (bet === "slipping") {
                    lost += 1;
                    slipping += 1;
                    slipAlive = false;
                } else if (bet === "tight") {
                    // VOID refund — neutral.
                } else {
                    // Couldn't decode — assume still alive at face odds.
                    projected *= odds;
                }
            } else {
                pendingKickoff += 1;
                projected *= odds;
            }
        }

        return {
            total: picks.length,
            live,
            onTrack,
            slipping,
            won,
            lost,
            pendingKickoff,
            slipAlive,
            projected: slipAlive ? projected : 0,
        };
    }, [tiers, liveByFixture, userLevel]);

    if (!stats) return null;
    const hasMotion = stats.live > 0 || stats.won + stats.lost > 0;

    return (
        <section
            className="max-w-7xl mx-auto px-4 md:px-8 mt-8"
            data-testid="slip-scoreboard"
        >
            <div className="bg-card border border-border p-5 md:p-6">
                <div className="flex items-center justify-between mb-4 gap-3">
                    <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-primary">
                        ▌ Today's slip · live
                    </h2>
                    <div className="flex items-center gap-3">
                        {alerts?.supported && (
                            <AlertsToggle alerts={alerts} />
                        )}
                        <span
                            className="font-mono text-[10px] uppercase tracking-widest text-zinc-500"
                            data-testid="slip-scoreboard-total"
                        >
                            {stats.total} picks
                        </span>
                    </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
                    <Stat
                        label="In play"
                        value={stats.live}
                        sub={
                            stats.pendingKickoff
                                ? `${stats.pendingKickoff} upcoming`
                                : null
                        }
                        accent={hasMotion ? "text-red-400" : "text-zinc-400"}
                        testId="slip-scoreboard-live"
                    />
                    <Stat
                        label="On track"
                        value={stats.onTrack}
                        sub={
                            stats.won
                                ? `${stats.won} won · ${stats.live ? "live" : "settled"}`
                                : null
                        }
                        accent="text-primary"
                        testId="slip-scoreboard-on-track"
                    />
                    <Stat
                        label="Slipping"
                        value={stats.slipping}
                        sub={stats.lost ? `${stats.lost} lost` : null}
                        accent={
                            stats.slipping > 0
                                ? "text-red-300"
                                : "text-zinc-400"
                        }
                        testId="slip-scoreboard-slipping"
                    />
                    <Stat
                        label={stats.slipAlive ? "Projected return" : "Slip busted"}
                        value={
                            stats.slipAlive
                                ? `${stats.projected.toFixed(2)}x`
                                : "—"
                        }
                        sub={
                            stats.slipAlive
                                ? "if every alive pick lands"
                                : "at least one pick lost"
                        }
                        accent={
                            stats.slipAlive
                                ? "text-primary"
                                : "text-red-300"
                        }
                        testId="slip-scoreboard-projected"
                    />
                </div>
            </div>
        </section>
    );
}

function Stat({ label, value, sub, accent, testId }) {
    return (
        <div data-testid={testId}>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                {label}
            </div>
            <div
                className={`font-mono text-3xl md:text-4xl font-black tabular-nums ${accent}`}
                data-testid={`${testId}-value`}
            >
                {value}
            </div>
            {sub && (
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                    {sub}
                </div>
            )}
        </div>
    );
}

function AlertsToggle({ alerts }) {
    const enabled = alerts.enabled;
    const denied = alerts.permission === "denied";
    if (denied) {
        return (
            <span
                className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-500"
                title="Browser notifications are blocked for this site. Re-enable them in your browser settings."
                data-testid="slip-alerts-blocked"
            >
                <BellSlash size={12} weight="bold" />
                Alerts blocked
            </span>
        );
    }
    if (enabled) {
        return (
            <span
                className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-primary"
                data-testid="slip-alerts-enabled"
            >
                <Bell size={12} weight="fill" />
                Alerts on
            </span>
        );
    }
    return (
        <button
            type="button"
            onClick={() => alerts.requestPermission()}
            className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:text-primary transition-snappy border border-border hover:border-primary px-2 py-1"
            data-testid="slip-alerts-enable-btn"
        >
            <Bell size={12} weight="bold" />
            Enable alerts
        </button>
    );
}
